import { useState, useEffect, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { transferRequestsAPI, productsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatNumber, formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  FileText, 
  Plus, 
  X, 
  Check, 
  Clock, 
  Warning, 
  Truck, 
  Package,
  CheckCircle,
  XCircle,
  HourglassHigh,
  MagnifyingGlass
} from '@phosphor-icons/react';

const statusConfig = {
  pending: { label: 'Beklemede', color: 'bg-accent/20 text-accent-foreground', icon: Clock },
  approved: { label: 'Onaylandı', color: 'bg-blue-100 text-blue-700', icon: Check },
  completed: { label: 'Tamamlandı', color: 'bg-secondary/20 text-secondary', icon: CheckCircle },
  rejected: { label: 'Reddedildi', color: 'bg-destructive/20 text-destructive', icon: XCircle },
  cancelled: { label: 'İptal Edildi', color: 'bg-orange-100 text-orange-700', icon: XCircle },
};

const urgencyConfig = {
  normal: { label: 'Normal', color: 'bg-muted text-muted-foreground' },
  urgent: { label: 'Acil', color: 'bg-red-100 text-red-700' },
};

export default function TransferRequests() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  const [formData, setFormData] = useState({
    items: [],
    notes: '',
    urgency: 'normal',
    request_date: new Date().toISOString().split('T')[0],
  });
  const [itemForm, setItemForm] = useState({ 
    product_id: '', 
    quantity: '',
    transfer_unit: 'adet',  // 'adet' veya 'koli'
    conversion_factor: null,  // 1 koli = X adet
  });
  
  const [adminNotes, setAdminNotes] = useState('');
  const [productSearchQuery, setProductSearchQuery] = useState('');

  // Seçilen ürünün dönüşüm bilgilerini hesapla
  const selectedProduct = products.find(p => p.id === itemForm.product_id);
  const hasConversionFactor = selectedProduct?.conversion_factor && selectedProduct.conversion_factor > 0;
  
  // Koli/adet fiyat hesaplamaları
  const unitPrice = selectedProduct?.unit_price || 0;
  const casePrice = hasConversionFactor ? unitPrice * selectedProduct.conversion_factor : null;
  
  // Sevk miktarı hesaplama
  const enteredQuantity = parseFloat(itemForm.quantity) || 0;
  const baseQuantity = itemForm.transfer_unit === 'koli' && hasConversionFactor 
    ? enteredQuantity * selectedProduct.conversion_factor 
    : enteredQuantity;
  const totalCost = unitPrice * baseQuantity;

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const status = activeTab === 'all' ? null : activeTab;
      const [requestsRes, productsRes] = await Promise.all([
        transferRequestsAPI.getAll(status),
        productsAPI.getAll(),
      ]);
      setRequests(requestsRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setFormData({ items: [], notes: '', urgency: 'normal', request_date: new Date().toISOString().split('T')[0] });
    setItemForm({ product_id: '', quantity: '' });
    setDialogOpen(true);
  };

  const handleAddItem = () => {
    if (!itemForm.product_id || !itemForm.quantity) {
      toast.error('Ürün ve miktar seçin');
      return;
    }
    
    const product = products.find(p => p.id === itemForm.product_id);
    const qty = parseFloat(itemForm.quantity);
    const transferUnit = itemForm.transfer_unit || 'adet';
    
    // Koli seçilmişse dönüşüm katsayısı kontrolü
    if (transferUnit === 'koli') {
      if (!product.conversion_factor || product.conversion_factor <= 0) {
        toast.error(`'${product.name}' için koli dönüşüm katsayısı tanımlı değil. Önce ürün ayarlarından tanımlayın.`);
        return;
      }
    }
    
    // Ana birime dönüştürülmüş miktar
    const conversionFactor = transferUnit === 'koli' ? product.conversion_factor : null;
    const baseQty = transferUnit === 'koli' && conversionFactor ? qty * conversionFactor : qty;
    
    // Stok kontrolü depo stok birimi cinsinden (koli depoda: koli, aksi halde adet)
    const stockInKoli = product.stock_unit === 'koli' && product.conversion_factor > 0;
    const requiredStock = stockInKoli
      ? (transferUnit === 'koli' ? qty : qty / product.conversion_factor)
      : baseQty;
    const stockUnitLabel = stockInKoli ? 'koli' : product.unit;
    if (requiredStock > product.warehouse_stock + 1e-9) {
      toast.error(`Depoda yeterli stok yok (${formatNumber(product.warehouse_stock)} ${stockUnitLabel} mevcut, ${formatNumber(requiredStock)} ${stockUnitLabel} gerekli)`);
      return;
    }
    
    const existing = formData.items.find(i => i.product_id === itemForm.product_id);
    if (existing) {
      toast.error('Bu ürün zaten ekli');
      return;
    }
    
    const unitPriceVal = product.unit_price || 0;
    const casePriceVal = conversionFactor ? unitPriceVal * conversionFactor : null;
    const totalCostVal = unitPriceVal * baseQty;
    
    setFormData({
      ...formData,
      items: [...formData.items, {
        product_id: itemForm.product_id,
        product_name: product.name,
        quantity: qty,
        unit: product.unit,
        warehouse_stock: product.warehouse_stock,
        // Birim dönüşüm bilgileri
        transfer_unit: transferUnit,
        conversion_factor: conversionFactor,
        base_quantity: baseQty,
        unit_price: unitPriceVal,
        case_price: casePriceVal,
        total_cost: totalCostVal,
      }]
    });
    setItemForm({ product_id: '', quantity: '', transfer_unit: 'adet', conversion_factor: null });
  };

  const handleRemoveItem = (productId) => {
    setFormData({
      ...formData,
      items: formData.items.filter(i => i.product_id !== productId)
    });
  };

  const handleSubmitRequest = async () => {
    if (formData.items.length === 0) {
      toast.error('En az bir ürün ekleyin');
      return;
    }
    
    try {
      // Birim dönüşüm bilgilerini de gönder
      await transferRequestsAPI.create({
        items: formData.items.map(i => ({ 
          product_id: i.product_id, 
          quantity: i.quantity,
          transfer_unit: i.transfer_unit || 'adet',
          conversion_factor: i.conversion_factor || null,
        })),
        notes: formData.notes || undefined,
        urgency: formData.urgency,
        request_date: formData.request_date,
      });
      toast.success('Sevk talebi oluşturuldu');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Talep oluşturulamadı');
    }
  };

  const handleApprove = async (requestId) => {
    try {
      await transferRequestsAPI.update(requestId, {
        status: 'approved',
        admin_notes: adminNotes || undefined,
      });
      toast.success('Talep onaylandı ve stok transferi yapıldı');
      setDetailDialogOpen(false);
      setAdminNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Onaylama başarısız');
    }
  };

  const handleReject = async (requestId) => {
    if (!adminNotes) {
      toast.error('Red nedeni yazın');
      return;
    }
    
    try {
      await transferRequestsAPI.update(requestId, {
        status: 'rejected',
        admin_notes: adminNotes,
      });
      toast.success('Talep reddedildi');
      setDetailDialogOpen(false);
      setAdminNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  // İptal işlemi
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancellingRequest, setCancellingRequest] = useState(null);
  const [restoreStock, setRestoreStock] = useState(true);

  const handleOpenCancelDialog = (request) => {
    setCancellingRequest(request);
    setCancelReason('');
    setRestoreStock(request.status === 'completed'); // Completed ise varsayılan olarak stok iadesi yap
    setCancelDialogOpen(true);
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      toast.error('İptal nedeni yazın');
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/transfer-requests/${cancellingRequest.id}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          reason: cancelReason,
          restore_stock: restoreStock,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.stock_restored ? 'Talep iptal edildi ve stok iade edildi' : 'Talep iptal edildi');
        setCancelDialogOpen(false);
        setDetailDialogOpen(false);
        setCancelReason('');
        setCancellingRequest(null);
        fetchData();
      } else {
        toast.error(data.detail || 'İptal işlemi başarısız');
      }
    } catch (error) {
      toast.error('İptal işlemi başarısız');
    }
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'manager';
  const warehouseProducts = products.filter(p => p.warehouse_stock > 0);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">
              Sevk İrsaliyesi
            </h1>
            <p className="mt-2 text-muted-foreground">
              Depodan mağazaya sevk talepleri
            </p>
          </div>
          <Button onClick={handleOpenDialog} className="bg-primary hover:bg-primary/90" data-testid="create-transfer-request-btn">
            <Plus size={20} className="mr-2" />
            Yeni Talep
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all">Tümü</TabsTrigger>
            <TabsTrigger value="pending">Bekleyen</TabsTrigger>
            <TabsTrigger value="completed">Tamamlanan</TabsTrigger>
            <TabsTrigger value="rejected">Reddedilen</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-6">
            <div className="border border-border bg-card rounded-xl overflow-x-auto">
              <Table className="data-table min-w-[800px]">
                <TableHeader>
                  <TableRow className="border-b border-border bg-muted/30">
                    <TableHead className="py-4 whitespace-nowrap">Talep No</TableHead>
                    <TableHead className="whitespace-nowrap">Talep Eden</TableHead>
                    <TableHead className="whitespace-nowrap">Ürünler</TableHead>
                    <TableHead className="whitespace-nowrap">Aciliyet</TableHead>
                    <TableHead className="whitespace-nowrap">Durum</TableHead>
                    <TableHead className="whitespace-nowrap">Tarih</TableHead>
                    <TableHead className="text-right whitespace-nowrap sticky right-0 bg-muted/30">İşlem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12">
                        <FileText size={48} weight="duotone" className="mx-auto text-muted-foreground/50 mb-4" />
                        <p className="text-muted-foreground">Henüz talep bulunmuyor</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    requests.map((req) => {
                      const status = statusConfig[req.status] || statusConfig.pending;
                      const urgency = urgencyConfig[req.urgency] || urgencyConfig.normal;
                      const StatusIcon = status.icon;
                      
                      return (
                        <TableRow key={req.id} className="table-row-hover border-b border-border/40" data-testid={`request-row-${req.id}`}>
                          <TableCell className="py-3 font-mono text-sm">#{req.id.slice(0, 8)}</TableCell>
                          <TableCell className="font-medium">{req.created_by_name}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {req.items.slice(0, 2).map((item, idx) => (
                                <span key={idx} className="text-xs bg-muted px-2 py-1 rounded">
                                  {item.product_name} x{item.quantity}
                                </span>
                              ))}
                              {req.items.length > 2 && (
                                <span className="text-xs bg-muted px-2 py-1 rounded">+{req.items.length - 2}</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className={`badge-pill ${urgency.color}`}>{urgency.label}</span>
                          </TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center gap-1 badge-pill ${status.color}`}>
                              <StatusIcon size={14} weight="fill" />
                              {status.label}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{formatDate(req.created_at)}</TableCell>
                          <TableCell className="text-right sticky right-0 bg-card">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedRequest(req);
                                setAdminNotes('');
                                setDetailDialogOpen(true);
                              }}
                            >
                              Detay
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>

        {/* Create Request Dialog */}
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setProductSearchQuery('');
        }}>
          <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="new-request-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Yeni Sevk Talebi</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 min-w-0">
              {/* Tarih Seçimi */}
              <div className="space-y-2">
                <Label>Sevk Tarihi</Label>
                <Input
                  type="date"
                  value={formData.request_date}
                  onChange={(e) => setFormData({ ...formData, request_date: e.target.value })}
                  data-testid="request-date-input"
                />
                <p className="text-xs text-muted-foreground">Geçmiş tarihli sevk girişi yapabilirsiniz</p>
              </div>
              
              <div className="space-y-2">
                <Label>Ürün Ara ve Ekle</Label>
                
                {/* Ürün Arama */}
                <div className="relative mb-2">
                  <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Ürün adı ile ara..."
                    value={productSearchQuery}
                    onChange={(e) => setProductSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="product-search-input"
                  />
                </div>
                
                <div className="flex flex-col sm:flex-row gap-2 min-w-0">
                  <Select value={itemForm.product_id} onValueChange={(v) => {
                    const product = products.find(p => p.id === v);
                    setItemForm({ 
                      ...itemForm, 
                      product_id: v,
                      // Dönüşüm katsayısı varsa koli seçeneği aktif olsun
                      transfer_unit: 'adet',
                      conversion_factor: product?.conversion_factor || null,
                    });
                  }}>
                    <SelectTrigger className="flex-1 min-w-0 [&>span]:truncate [&>span]:block [&>span]:text-left" data-testid="request-product-select">
                      <SelectValue placeholder="Ürün seçin" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60 max-w-[calc(100vw-3rem)]">
                      {warehouseProducts
                        .filter(p => 
                          !productSearchQuery || 
                          p.name.toLowerCase().includes(productSearchQuery.toLowerCase())
                        )
                        .map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            <div className="flex flex-wrap justify-between items-center w-full gap-x-2">
                              <span className="break-words">{p.name}</span>
                              <span className="text-xs text-muted-foreground">
                                Stok: {formatNumber(p.warehouse_stock)} {p.stock_unit === 'koli' ? 'koli' : p.unit} | ₺{(p.unit_price || 0).toLocaleString('tr-TR')}
                                {p.conversion_factor > 0 && ` (1 koli=${p.conversion_factor} adet)`}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  
                  {/* Birim Seçici - Sadece ürün seçildiğinde görünür */}
                  <div className="flex gap-2 shrink-0">
                  {itemForm.product_id && (
                    <Select 
                      value={itemForm.transfer_unit} 
                      onValueChange={(v) => setItemForm({ ...itemForm, transfer_unit: v })}
                    >
                      <SelectTrigger className="w-28" data-testid="request-unit-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="adet">Adet</SelectItem>
                        <SelectItem 
                          value="koli" 
                          disabled={!hasConversionFactor}
                        >
                          Koli {!hasConversionFactor && '(tanımsız)'}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Miktar"
                    className="flex-1 sm:w-24 sm:flex-none min-w-0"
                    value={itemForm.quantity}
                    onChange={(e) => setItemForm({ ...itemForm, quantity: e.target.value })}
                    data-testid="request-quantity-input"
                  />
                  <Button type="button" onClick={handleAddItem} className="shrink-0" data-testid="add-request-item-btn">
                    <Plus size={18} />
                  </Button>
                  </div>
                </div>
                
                {/* Seçilen ürünün detaylı maliyet önizlemesi */}
                {itemForm.product_id && (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm space-y-1 [&>div]:flex-wrap [&>div]:gap-x-2" data-testid="request-cost-preview">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Adet Fiyatı:</span>
                      <span className="font-medium">₺{unitPrice.toLocaleString('tr-TR', {minimumFractionDigits: 2})}</span>
                    </div>
                    {hasConversionFactor && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Koli Fiyatı (1 koli = {selectedProduct.conversion_factor} adet):</span>
                        <span className="font-medium">₺{casePrice.toLocaleString('tr-TR', {minimumFractionDigits: 2})}</span>
                      </div>
                    )}
                    {!hasConversionFactor && itemForm.transfer_unit === 'koli' && (
                      <div className="text-amber-600 flex items-center gap-1">
                        <Warning size={14} />
                        Koli dönüşümü tanımlı değil. Ürün ayarlarından tanımlayın.
                      </div>
                    )}
                    {itemForm.quantity && enteredQuantity > 0 && (
                      <>
                        <div className="border-t border-blue-200 my-2" />
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Sevk Miktarı:</span>
                          <span className="font-medium">
                            {enteredQuantity} {itemForm.transfer_unit}
                            {itemForm.transfer_unit === 'koli' && hasConversionFactor && (
                              <span className="text-muted-foreground ml-1">= {baseQuantity} adet</span>
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between text-blue-700 font-semibold">
                          <span>Toplam Maliyet:</span>
                          <span>₺{totalCost.toLocaleString('tr-TR', {minimumFractionDigits: 2})}</span>
                        </div>
                      </>
                    )}
                  </div>
                )}
                
                {productSearchQuery && (
                  <p className="text-xs text-muted-foreground">
                    {warehouseProducts.filter(p => p.name.toLowerCase().includes(productSearchQuery.toLowerCase())).length} ürün bulundu
                  </p>
                )}
              </div>

              {formData.items.length > 0 && (
                <div className="border rounded-lg overflow-x-auto">
                  <Table className="text-sm">
                    <TableHeader>
                      <TableRow className="bg-muted/30">
                        <TableHead>Ürün</TableHead>
                        <TableHead className="text-right">Talep</TableHead>
                        <TableHead className="text-right hidden sm:table-cell">Ana Birim</TableHead>
                        <TableHead className="text-right hidden sm:table-cell">Birim Fiyat</TableHead>
                        <TableHead className="text-right">Maliyet</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {formData.items.map((item) => (
                        <TableRow key={item.product_id}>
                          <TableCell className="max-w-[180px] break-words">
                            {item.product_name}
                            {item.transfer_unit === 'koli' && item.conversion_factor && (
                              <span className="text-xs text-muted-foreground block">
                                (1 koli = {item.conversion_factor} adet)
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-data">
                            {formatNumber(item.quantity)} {item.transfer_unit || 'adet'}
                          </TableCell>
                          <TableCell className="text-right font-data text-muted-foreground hidden sm:table-cell">
                            {item.base_quantity ? formatNumber(item.base_quantity) : formatNumber(item.quantity)} adet
                          </TableCell>
                          <TableCell className="text-right font-data text-muted-foreground hidden sm:table-cell">₺{(item.unit_price || 0).toLocaleString('tr-TR')}</TableCell>
                          <TableCell className="text-right font-data font-semibold text-blue-600">₺{(item.total_cost || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</TableCell>
                          <TableCell>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleRemoveItem(item.product_id)}>
                              <X size={16} />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-muted/50 font-bold">
                        <TableCell colSpan={2} className="sm:hidden">TOPLAM</TableCell>
                        <TableCell colSpan={4} className="hidden sm:table-cell">TOPLAM MALİYET</TableCell>
                        <TableCell className="text-right text-blue-700">
                          ₺{formData.items.reduce((sum, item) => sum + (item.total_cost || 0), 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})}
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Aciliyet</Label>
                  <Select value={formData.urgency} onValueChange={(v) => setFormData({ ...formData, urgency: v })}>
                    <SelectTrigger data-testid="request-urgency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="urgent">Acil</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Not</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Talep notu..."
                  rows={2}
                  data-testid="request-notes-input"
                />
              </div>

              <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
                <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="new-request-cancel-btn">İptal</Button>
                <Button onClick={handleSubmitRequest} className="bg-primary" data-testid="submit-request-btn">
                  <Truck size={18} className="mr-2" />
                  Talep Oluştur
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>

        {/* Detail Dialog */}
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="sm:max-w-xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                Sevk Talebi #{selectedRequest?.id.slice(0, 8)}
              </DialogTitle>
            </DialogHeader>
            {selectedRequest && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Talep Eden:</span>
                    <span className="ml-2 font-medium">{selectedRequest.created_by_name}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Tarih:</span>
                    <span className="ml-2">{formatDate(selectedRequest.created_at)}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Aciliyet:</span>
                    <span className={`ml-2 badge-pill ${urgencyConfig[selectedRequest.urgency]?.color}`}>
                      {urgencyConfig[selectedRequest.urgency]?.label}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Durum:</span>
                    <span className={`ml-2 badge-pill ${statusConfig[selectedRequest.status]?.color}`}>
                      {statusConfig[selectedRequest.status]?.label}
                    </span>
                  </div>
                </div>

                {selectedRequest.notes && (
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-1">Talep Notu:</p>
                    <p className="text-sm">{selectedRequest.notes}</p>
                  </div>
                )}

                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <TableHead>Ürün</TableHead>
                      <TableHead className="text-right">Miktar</TableHead>
                      <TableHead className="text-right">Depo Stoku</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedRequest.items.map((item) => (
                      <TableRow key={item.product_id}>
                        <TableCell className="font-medium">{item.product_name}</TableCell>
                        <TableCell className="text-right font-data">{formatNumber(item.quantity)} {item.unit}</TableCell>
                        <TableCell className="text-right font-data text-muted-foreground">{formatNumber(item.warehouse_stock)} {item.unit}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {selectedRequest.admin_notes && (
                  <div className="p-3 bg-accent/10 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-1">Yönetici Notu:</p>
                    <p className="text-sm">{selectedRequest.admin_notes}</p>
                  </div>
                )}

                {isAdmin && selectedRequest.status === 'pending' && (
                  <>
                    <div className="space-y-2">
                      <Label>Yönetici Notu</Label>
                      <Textarea
                        value={adminNotes}
                        onChange={(e) => setAdminNotes(e.target.value)}
                        placeholder="Not ekleyin (red için zorunlu)..."
                        rows={2}
                        data-testid="admin-notes-input"
                      />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button
                        variant="outline"
                        onClick={() => handleReject(selectedRequest.id)}
                        className="text-destructive border-destructive hover:bg-destructive/10"
                        data-testid="reject-request-btn"
                      >
                        <XCircle size={18} className="mr-2" />
                        Reddet
                      </Button>
                      <Button
                        onClick={() => handleApprove(selectedRequest.id)}
                        className="bg-secondary hover:bg-secondary/90"
                        data-testid="approve-request-btn"
                      >
                        <CheckCircle size={18} className="mr-2" />
                        Onayla & Transfer Et
                      </Button>
                    </div>
                  </>
                )}

                {/* İptal butonu - completed veya pending durumda göster, cancelled/rejected hariç */}
                {isAdmin && (selectedRequest.status === 'completed' || selectedRequest.status === 'pending') && (
                  <div className="border-t pt-4 mt-4">
                    <Button
                      variant="outline"
                      onClick={() => handleOpenCancelDialog(selectedRequest)}
                      className="w-full text-orange-600 border-orange-300 hover:bg-orange-50"
                      data-testid="cancel-request-btn"
                    >
                      <XCircle size={18} className="mr-2" />
                      {selectedRequest.status === 'completed' ? 'İptal Et ve Stok İade Et' : 'Talebi İptal Et'}
                    </Button>
                  </div>
                )}

                {/* İptal edilmiş talep bilgisi */}
                {selectedRequest.status === 'cancelled' && (
                  <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <p className="text-xs text-orange-600 mb-1 font-semibold">İptal Bilgisi:</p>
                    <p className="text-sm text-orange-700">
                      <strong>Neden:</strong> {selectedRequest.cancel_reason || 'Belirtilmemiş'}
                    </p>
                    <p className="text-sm text-orange-700">
                      <strong>İptal Eden:</strong> {selectedRequest.cancelled_by_name || 'Bilinmiyor'}
                    </p>
                    {selectedRequest.cancelled_at && (
                      <p className="text-sm text-orange-700">
                        <strong>Tarih:</strong> {formatDate(selectedRequest.cancelled_at)}
                      </p>
                    )}
                    {selectedRequest.stock_restored && (
                      <p className="text-sm text-green-600 mt-1">
                        <CheckCircle size={14} className="inline mr-1" />
                        Stok iadesi yapıldı
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* İptal Dialog */}
        <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-orange-600">
                Sevk Talebini İptal Et
              </DialogTitle>
            </DialogHeader>
            {cancellingRequest && (
              <div className="space-y-4">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    <strong>Talep:</strong> #{cancellingRequest.id.slice(0, 8)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <strong>Durum:</strong> {statusConfig[cancellingRequest.status]?.label}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <strong>Ürün Sayısı:</strong> {cancellingRequest.items?.length || 0}
                  </p>
                </div>

                {cancellingRequest.status === 'completed' && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Warning size={18} className="text-amber-600 mt-0.5" />
                      <div>
                        <p className="text-sm text-amber-700 font-medium">Bu talep tamamlanmış ve stoklar değişmiş</p>
                        <p className="text-xs text-amber-600 mt-1">
                          İptal edilirse mağazadan depoya stok iadesi yapılacak.
                        </p>
                        <div className="mt-2 flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="restoreStock"
                            checked={restoreStock}
                            onChange={(e) => setRestoreStock(e.target.checked)}
                            className="rounded"
                          />
                          <label htmlFor="restoreStock" className="text-sm text-amber-700">
                            Stok iadesini uygula
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="cancelReason">İptal Nedeni <span className="text-destructive">*</span></Label>
                  <Textarea
                    id="cancelReason"
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="İptal nedenini yazın..."
                    rows={3}
                    data-testid="cancel-reason-input"
                  />
                </div>

                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>
                    Vazgeç
                  </Button>
                  <Button
                    onClick={handleCancel}
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                    data-testid="confirm-cancel-btn"
                  >
                    <XCircle size={18} className="mr-2" />
                    İptal Et
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
